/**
 * gstr2b.ts — Parse the GST portal's GSTR-2B JSON (auto-drafted ITC statement)
 * and reconcile it against the taxpayer's own purchase records ("books").
 *
 * The reconciliation is the money-saving feature: it surfaces where a supplier
 * hasn't reported an invoice (ITC at risk), where the taxpayer never recorded a
 * purchase the supplier declared (unclaimed ITC), and where the two disagree on
 * tax (mismatch to investigate before claiming).
 *
 * Pure — parsing + matching only. File I/O and DB access live in the caller.
 */

import { round2 } from './gstReturns';

// ─── Normalised shapes ───────────────────────────────────────────────────────

export interface Gstr2bInvoice {
  supplierGstin: string;
  supplierName: string;
  invoiceNumber: string;
  date: string; // as printed in the JSON (DD-MM-YYYY), may be ''
  value: number;
  taxable: number;
  igst: number;
  cgst: number;
  sgst: number;
  cess: number;
  totalTax: number;
  itcAvailable: boolean;
  source: 'B2B' | 'CDNR';
}

export interface Gstr2bParsed {
  gstin?: string;
  period?: string; // MMYYYY
  invoices: Gstr2bInvoice[];
}

export interface BooksPurchase {
  supplierGstin: string | null;
  supplierName: string;
  invoiceNumber: string;
  date: Date | null;
  taxable: number;
  igst: number;
  cgst: number;
  sgst: number;
  cess: number;
  totalTax: number;
  value: number;
  poNumber: string;
}

// ─── Parsing ─────────────────────────────────────────────────────────────────

function num(v: unknown): number {
  const n = typeof v === 'string' ? parseFloat(v) : (v as number);
  return Number.isFinite(n) ? n : 0;
}

/** Sum item-level tax from a 2B invoice/note, tolerant of field-name variants. */
function sumItems(items: any[]): { taxable: number; igst: number; cgst: number; sgst: number; cess: number } {
  const acc = { taxable: 0, igst: 0, cgst: 0, sgst: 0, cess: 0 };
  for (const raw of items ?? []) {
    // 2B item detail is sometimes nested under `itm_det`.
    const it = raw.itm_det ?? raw;
    acc.taxable += num(it.txval);
    acc.igst += num(it.igst ?? it.iamt);
    acc.cgst += num(it.cgst ?? it.camt);
    acc.sgst += num(it.sgst ?? it.samt);
    acc.cess += num(it.cess ?? it.csamt);
  }
  return acc;
}

/**
 * Parse a GSTR-2B JSON string (or already-parsed object). Handles the portal's
 * optional `data` wrapper and both b2b invoices and cdnr credit notes.
 */
export function parseGstr2bJson(raw: string | Record<string, unknown>): Gstr2bParsed {
  let root: any;
  try {
    root = typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    throw new Error('File is not valid JSON. Download the GSTR-2B JSON from the GST portal.');
  }
  const data = root?.data ?? root;
  const docdata = data?.docdata ?? data?.docData;
  if (!docdata) {
    throw new Error('This does not look like a GSTR-2B JSON (no docdata section).');
  }

  const invoices: Gstr2bInvoice[] = [];

  const readParty = (party: any, source: 'B2B' | 'CDNR', docs: any[]) => {
    const supplierGstin = String(party.ctin ?? '').trim().toUpperCase();
    const supplierName = String(party.trdnm ?? party.trade_name ?? '').trim();
    for (const doc of docs ?? []) {
      const itcAvailable = String(doc.itcavl ?? 'Y').toUpperCase() !== 'N';
      const sums = sumItems(doc.items ?? doc.itms);
      const totalTax = sums.igst + sums.cgst + sums.sgst + sums.cess;
      invoices.push({
        supplierGstin,
        supplierName,
        invoiceNumber: String(doc.inum ?? doc.nt_num ?? '').trim(),
        date: String(doc.dt ?? doc.nt_dt ?? '').trim(),
        value: num(doc.val),
        taxable: round2(sums.taxable),
        igst: round2(sums.igst),
        cgst: round2(sums.cgst),
        sgst: round2(sums.sgst),
        cess: round2(sums.cess),
        totalTax: round2(totalTax),
        itcAvailable,
        source,
      });
    }
  };

  for (const party of docdata.b2b ?? []) readParty(party, 'B2B', party.inv);
  for (const party of docdata.cdnr ?? []) readParty(party, 'CDNR', party.nt);

  return {
    gstin: data?.gstin ? String(data.gstin) : undefined,
    period: data?.rtnprd ? String(data.rtnprd) : undefined,
    invoices,
  };
}

// ─── File / period guards ────────────────────────────────────────────────────

/** Format a 0-indexed month + year as the portal's MMYYYY return period. */
function toReturnPeriod(month: number, year: number): string {
  return `${String(month + 1).padStart(2, '0')}${year}`;
}

/** Human label for an MMYYYY period, e.g. "072026" → "July 2026". */
function periodLabel(period: string): string {
  const mm = Number(period.slice(0, 2));
  const yyyy = Number(period.slice(2));
  if (!mm || !yyyy) return period;
  return new Date(yyyy, mm - 1, 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
}

/**
 * Reject a GSTR-2B file that belongs to another return period or another
 * taxpayer. Both were previously parsed and thrown away, so reconciling June's
 * books against July's 2B reported every June purchase as "ITC at risk", and a
 * different taxpayer's file reconciled happily.
 *
 * Returns an error message, or null if the file is safe to reconcile.
 * `[from, to]` are the inclusive bounds of the selected books range.
 */
export function validateGstr2bFile(
  parsed: Gstr2bParsed,
  from: Date,
  to: Date,
  ownGstin: string | null | undefined,
): string | null {
  const own = ownGstin?.trim().toUpperCase();
  const fileGstin = parsed.gstin?.trim().toUpperCase();
  if (own && fileGstin && own !== fileGstin) {
    return `This GSTR-2B belongs to GSTIN ${fileGstin}, but this store files under ${own}.`;
  }

  if (parsed.period) {
    // GSTR-2B is a monthly statement — the selected range must sit inside the
    // single month the file covers.
    const fromPeriod = toReturnPeriod(from.getMonth(), from.getFullYear());
    const toPeriod = toReturnPeriod(to.getMonth(), to.getFullYear());
    if (fromPeriod !== parsed.period || toPeriod !== parsed.period) {
      return `This GSTR-2B is for ${periodLabel(parsed.period)}, but the selected period is ${
        fromPeriod === toPeriod ? periodLabel(fromPeriod) : `${periodLabel(fromPeriod)} – ${periodLabel(toPeriod)}`
      }. Select the matching month, or download the right file.`;
    }
  }

  return null;
}

// ─── Reconciliation ──────────────────────────────────────────────────────────

export type ReconStatus = 'MATCHED' | 'MISMATCH' | 'ONLY_IN_BOOKS' | 'ONLY_IN_2B';

export interface ReconAmounts {
  taxable: number;
  totalTax: number;
  value: number;
}

export interface ReconRow {
  status: ReconStatus;
  supplierGstin: string;
  supplierName: string;
  invoiceNumber: string;
  books?: ReconAmounts;
  twoB?: ReconAmounts & { itcAvailable: boolean; invoiceNumber?: string };
  /** books.totalTax − twoB.totalTax (only for matched/mismatch). */
  taxDiff?: number;
  /** How the match was found. FUZZY = number/date-window heuristic. */
  matchType?: 'EXACT' | 'FUZZY';
}

export interface ReconSummary {
  matched: number;
  /** Subset of matched/mismatch that were paired via the fuzzy heuristic. */
  fuzzyMatched: number;
  mismatch: number;
  onlyInBooks: number;
  onlyIn2b: number;
  /** ITC (tax) that reconciles cleanly — safe to claim. */
  itcMatched: number;
  /** ITC claimed in books but absent from 2B — at risk. */
  itcOnlyInBooks: number;
  /** ITC available in 2B but not yet recorded in books — unclaimed. */
  itcOnlyIn2b: number;
  /** Net tax difference across mismatched invoices (books − 2B). */
  itcMismatchDiff: number;
}

export interface ReconResult {
  rows: ReconRow[];
  summary: ReconSummary;
}

export interface ReconOptions {
  /** Absolute rupee tolerance when comparing tax/taxable. Default ₹1. */
  tolerance?: number;
  /** Enable fuzzy invoice-number + date-window matching. Default true. */
  fuzzy?: boolean;
  /** Max days between book date and 2B date for a fuzzy match. Default 15. */
  dateWindowDays?: number;
  /** Minimum invoice-number similarity (0–1) for a fuzzy match. Default 0.82. */
  invoiceSimilarityThreshold?: number;
}

/** Normalise an invoice number for matching: upper-case, alphanumeric only. */
export function normalizeInvoiceNumber(inv: string): string {
  return inv.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** Looser form that also drops leading zeros in digit runs (INV-007 → INV7). */
export function looseInvoiceNumber(inv: string): string {
  return normalizeInvoiceNumber(inv).replace(/\d+/g, (d) => String(parseInt(d, 10)));
}

function normalizeGstin(g: string | null | undefined): string {
  return (g ?? '').trim().toUpperCase();
}

function matchKey(gstin: string | null | undefined, inv: string): string {
  return `${normalizeGstin(gstin)}|${normalizeInvoiceNumber(inv)}`;
}

/** Levenshtein edit distance between two strings. */
function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  let curr = new Array<number>(n + 1);
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}

/** Invoice-number similarity in [0,1]: 1 when loosely equal, else edit-ratio. */
export function invoiceSimilarity(a: string, b: string): number {
  if (looseInvoiceNumber(a) === looseInvoiceNumber(b)) return 1;
  const na = normalizeInvoiceNumber(a);
  const nb = normalizeInvoiceNumber(b);
  const maxLen = Math.max(na.length, nb.length);
  if (maxLen === 0) return 0;
  return 1 - editDistance(na, nb) / maxLen;
}

/** Parse an Indian-format date string (DD-MM-YYYY or DD/MM/YYYY) → Date|null. */
export function parseIndianDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = s.trim().match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/);
  if (!m) return null;
  const day = parseInt(m[1], 10);
  const mon = parseInt(m[2], 10) - 1;
  let year = parseInt(m[3], 10);
  if (year < 100) year += 2000;
  const d = new Date(year, mon, day);
  return Number.isNaN(d.getTime()) ? null : d;
}

function daysBetween(a: Date, b: Date): number {
  return Math.abs(a.getTime() - b.getTime()) / (1000 * 60 * 60 * 24);
}

/**
 * Reconcile books purchases against a parsed GSTR-2B. Matches on
 * supplier GSTIN + invoice number, then compares tax within tolerance.
 */
export function reconcile2b(
  books: BooksPurchase[],
  twoB: Gstr2bInvoice[],
  opts: ReconOptions = {},
): ReconResult {
  const tol = opts.tolerance ?? 1;
  const fuzzy = opts.fuzzy !== false;
  const dateWindow = opts.dateWindowDays ?? 15;
  const simThreshold = opts.invoiceSimilarityThreshold ?? 0.82;
  const rows: ReconRow[] = [];

  // 2B entries with a consumed flag; exact-key and per-GSTIN indexes over them.
  const entries = twoB.map((t) => ({ t, consumed: false }));
  const exactByKey = new Map<string, number[]>();
  const byGstin = new Map<string, number[]>();
  entries.forEach((e, idx) => {
    const key = matchKey(e.t.supplierGstin, e.t.invoiceNumber);
    (exactByKey.get(key) ?? exactByKey.set(key, []).get(key)!).push(idx);
    const g = normalizeGstin(e.t.supplierGstin);
    (byGstin.get(g) ?? byGstin.set(g, []).get(g)!).push(idx);
  });

  const summary: ReconSummary = {
    matched: 0, fuzzyMatched: 0, mismatch: 0, onlyInBooks: 0, onlyIn2b: 0,
    itcMatched: 0, itcOnlyInBooks: 0, itcOnlyIn2b: 0, itcMismatchDiff: 0,
  };

  const takeExact = (b: BooksPurchase): number => {
    const list = exactByKey.get(matchKey(b.supplierGstin, b.invoiceNumber));
    if (!list) return -1;
    while (list.length) {
      const i = list.shift()!;
      if (!entries[i].consumed) return i;
    }
    return -1;
  };

  // Best remaining 2B candidate for the same supplier by invoice similarity,
  // corroborated by amount closeness or date proximity (guards false positives).
  const takeFuzzy = (b: BooksPurchase): number => {
    const g = normalizeGstin(b.supplierGstin);
    if (!g) return -1;
    const candidates = byGstin.get(g);
    if (!candidates) return -1;
    let best = -1;
    let bestScore = -1;
    for (const idx of candidates) {
      if (entries[idx].consumed) continue;
      const t = entries[idx].t;
      const sim = invoiceSimilarity(b.invoiceNumber, t.invoiceNumber);
      if (sim < simThreshold) continue;
      const amountClose = Math.abs(b.totalTax - t.totalTax) <= Math.max(tol, b.totalTax * 0.05);
      const tDate = parseIndianDate(t.date);
      const dateClose = b.date && tDate ? daysBetween(b.date, tDate) <= dateWindow : false;
      if (!amountClose && !dateClose) continue;
      // Rank primarily by invoice similarity, then amount, then date.
      const score = sim * 100 + (amountClose ? 10 : 0) + (dateClose ? 1 : 0);
      if (score > bestScore) { bestScore = score; best = idx; }
    }
    return best;
  };

  const classify = (b: BooksPurchase, matchIdx: number, matchType: 'EXACT' | 'FUZZY') => {
    entries[matchIdx].consumed = true;
    const match = entries[matchIdx].t;
    const taxDiff = round2(b.totalTax - match.totalTax);
    const taxableDiff = Math.abs(b.taxable - match.taxable);
    const isMatch = Math.abs(taxDiff) <= tol && taxableDiff <= tol;
    rows.push({
      status: isMatch ? 'MATCHED' : 'MISMATCH',
      supplierGstin: normalizeGstin(b.supplierGstin),
      supplierName: b.supplierName || match.supplierName,
      invoiceNumber: b.invoiceNumber,
      books: { taxable: round2(b.taxable), totalTax: round2(b.totalTax), value: round2(b.value) },
      twoB: { taxable: match.taxable, totalTax: match.totalTax, value: match.value, itcAvailable: match.itcAvailable, invoiceNumber: match.invoiceNumber },
      taxDiff,
      matchType,
    });
    if (matchType === 'FUZZY') summary.fuzzyMatched += 1;
    if (isMatch) {
      summary.matched += 1;
      summary.itcMatched = round2(summary.itcMatched + match.totalTax);
    } else {
      summary.mismatch += 1;
      summary.itcMismatchDiff = round2(summary.itcMismatchDiff + taxDiff);
    }
  };

  // Pass 1: exact key. Pass 2 (deferred): fuzzy over anything left unmatched.
  const unmatched: BooksPurchase[] = [];
  for (const b of books) {
    const idx = takeExact(b);
    if (idx >= 0) classify(b, idx, 'EXACT');
    else unmatched.push(b);
  }
  for (const b of unmatched) {
    const idx = fuzzy ? takeFuzzy(b) : -1;
    if (idx >= 0) {
      classify(b, idx, 'FUZZY');
    } else {
      rows.push({
        status: 'ONLY_IN_BOOKS',
        supplierGstin: normalizeGstin(b.supplierGstin),
        supplierName: b.supplierName,
        invoiceNumber: b.invoiceNumber,
        books: { taxable: round2(b.taxable), totalTax: round2(b.totalTax), value: round2(b.value) },
      });
      summary.onlyInBooks += 1;
      summary.itcOnlyInBooks = round2(summary.itcOnlyInBooks + b.totalTax);
    }
  }

  // Whatever remains in 2B was never matched → only in 2B.
  for (const e of entries) {
    if (e.consumed) continue;
    const t = e.t;
    rows.push({
      status: 'ONLY_IN_2B',
      supplierGstin: t.supplierGstin,
      supplierName: t.supplierName,
      invoiceNumber: t.invoiceNumber,
      twoB: { taxable: t.taxable, totalTax: t.totalTax, value: t.value, itcAvailable: t.itcAvailable },
    });
    summary.onlyIn2b += 1;
    summary.itcOnlyIn2b = round2(summary.itcOnlyIn2b + t.totalTax);
  }

  const order: Record<ReconStatus, number> = { MISMATCH: 0, ONLY_IN_BOOKS: 1, ONLY_IN_2B: 2, MATCHED: 3 };
  rows.sort((a, b) => order[a.status] - order[b.status] || a.supplierGstin.localeCompare(b.supplierGstin));

  return { rows, summary };
}

// ─── CSV export ──────────────────────────────────────────────────────────────

function csvCell(v: string | number): string {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Flatten a reconciliation into a CSV a CA can open in Excel. */
export function reconResultToCsv(result: ReconResult): string {
  const header = [
    'Status', 'Match', 'Supplier GSTIN', 'Supplier', 'Books Invoice No', '2B Invoice No',
    'Books Taxable', 'Books Tax', 'GSTR-2B Taxable', 'GSTR-2B Tax', 'Tax Diff', 'ITC Available',
  ];
  const lines = [header.join(',')];
  for (const r of result.rows) {
    lines.push([
      r.status,
      r.matchType ?? '',
      r.supplierGstin,
      r.supplierName,
      r.invoiceNumber,
      r.twoB?.invoiceNumber ?? '',
      r.books?.taxable ?? '',
      r.books?.totalTax ?? '',
      r.twoB?.taxable ?? '',
      r.twoB?.totalTax ?? '',
      r.taxDiff ?? '',
      r.twoB ? (r.twoB.itcAvailable ? 'Y' : 'N') : '',
    ].map(csvCell).join(','));
  }
  return lines.join('\n');
}
